import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
/** Per-organization role (distinct from the platform ADMIN/CLIENT role). */
export const OrganizationRoleSchema = z.enum(['ADMIN', 'EDITOR', 'VIEWER']);
export const OrganizationMemberSchema = z.object({
    id: z.string(),
    userId: z.string(),
    organizationId: z.string(),
    role: OrganizationRoleSchema,
    email: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    createdAt: z.string(),
    updatedAt: z.string(),
    isDisabled: z.boolean(),
    allowedSitesCount: z.number(),
});
export const PendingInvitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: z.string(),
    status: z.string(),
    createdAt: z.string(),
});
export const InvitationDetailSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: z.string(),
    status: z.string(),
    createdAt: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    tenantSlug: z.string().nullable(),
});
export const SendInvitationBodySchema = z.object({
    email: z.string().email(),
    role: OrganizationRoleSchema.optional(),
});
export const UpdateMemberRoleBodySchema = z.object({
    role: OrganizationRoleSchema,
});
export const UpdateMemberNameBodySchema = z.object({
    firstName: z.string().trim().min(1),
    lastName: z.string().trim().min(1),
});
export const UpdateMemberAllowedSitesBodySchema = z.object({
    siteIds: z.array(z.string().min(1)),
});
const memberParams = z.object({
    organizationId: z.string().min(1),
    memberId: z.string().min(1),
});
const successBody = z.object({ success: z.boolean() });
export const organizationMembersContract = c.router({
    listMembers: {
        method: 'GET',
        path: '/organization/:organizationId/members',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: z.array(OrganizationMemberSchema) },
        summary: 'List the members of an organization',
    },
    /** @deprecated `myRole` travels with `getCurrentOrganization` — kept one release for cached front bundles. */
    getCurrentUserRole: {
        method: 'GET',
        path: '/organization/:organizationId/members/me/role',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: {
            200: z.object({ role: OrganizationRoleSchema.nullable() }),
        },
        summary: 'Role of the current user in the organization',
    },
    updateMemberRole: {
        method: 'PUT',
        path: '/organization/:organizationId/members/:memberId/role',
        pathParams: memberParams,
        body: UpdateMemberRoleBodySchema,
        responses: { 200: successBody },
        summary: 'Change the role of a member',
    },
    updateMemberName: {
        method: 'PUT',
        path: '/organization/:organizationId/members/:memberId/name',
        pathParams: memberParams,
        body: UpdateMemberNameBodySchema,
        responses: { 200: successBody },
        summary: 'Rename a member of the organization',
    },
    getMemberAllowedSites: {
        method: 'GET',
        path: '/organization/:organizationId/members/:memberId/allowed-sites',
        pathParams: memberParams,
        responses: { 200: z.array(z.string()) },
        summary: 'Billing centres a member is restricted to',
    },
    updateMemberAllowedSites: {
        method: 'PUT',
        path: '/organization/:organizationId/members/:memberId/allowed-sites',
        pathParams: memberParams,
        body: UpdateMemberAllowedSitesBodySchema,
        responses: { 200: successBody },
        summary: 'Replace the billing centres a member is restricted to',
    },
    disableMember: {
        method: 'POST',
        path: '/organization/:organizationId/members/:memberId/disable',
        pathParams: memberParams,
        body: c.noBody(),
        responses: { 201: successBody },
        summary: 'Disable a member',
    },
    enableMember: {
        method: 'POST',
        path: '/organization/:organizationId/members/:memberId/enable',
        pathParams: memberParams,
        body: c.noBody(),
        responses: { 201: successBody },
        summary: 'Enable a member',
    },
    deleteMember: {
        method: 'DELETE',
        path: '/organization/:organizationId/members/:memberId',
        pathParams: memberParams,
        body: c.noBody(),
        responses: { 200: successBody },
        summary: 'Remove a member from the organization',
    },
    sendInvitation: {
        method: 'POST',
        path: '/organization/:organizationId/invitations',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        body: SendInvitationBodySchema,
        responses: { 201: successBody },
        summary: 'Invite a user to the organization',
    },
    listPendingInvitations: {
        method: 'GET',
        path: '/organization/:organizationId/invitations/pending',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: z.array(PendingInvitationSchema) },
        summary: 'List the pending invitations of an organization',
    },
    /** Public endpoint (consumed before authentication). */
    getInvitation: {
        method: 'GET',
        path: '/invitation/:invitationId',
        pathParams: z.object({ invitationId: z.string().min(1) }),
        responses: { 200: InvitationDetailSchema },
        summary: 'Get an invitation by id (public)',
    },
    deleteInvitation: {
        method: 'DELETE',
        path: '/invitation/:invitationId',
        pathParams: z.object({ invitationId: z.string().min(1) }),
        body: c.noBody(),
        responses: { 200: successBody },
        summary: 'Delete a pending invitation of an organization the caller belongs to',
    },
});
